import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, Transition } from "@headlessui/react";
import { DotsHorizontalIcon } from "@heroicons/react/outline";
import { joinClassNames } from "../../../utils/converter";

export default function ActionsMenu({ children, extraClass  = 'right-0 ', classExtra = 'z-[1200]', baseClass='inline-block z-50', icon = DotsHorizontalIcon }) {
  const hasActiveChildren =
    React.Children.map(children, Boolean).filter(Boolean).length > 0;

  const Icon = icon;
  const buttonRef = useRef(null);
  const itemsRef = useRef(null);
  const [menuStyles, setMenuStyles] = useState({ top: 0, left: 0 });
  const [openDirection, setOpenDirection] = useState('down');
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const updatePosition = useCallback(() => {
    const buttonEl = buttonRef.current;
    const itemsEl = itemsRef.current;

    if (!buttonEl || !itemsEl) {
      return;
    }

    const buttonRect = buttonEl.getBoundingClientRect();
    const menuRect = itemsEl.getBoundingClientRect();
    const spacing = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let shouldOpenUp = spaceBelow < menuRect.height && spaceAbove > spaceBelow;
    if (menuRect.height + spacing * 2 > viewportHeight) {
      shouldOpenUp = false;
    }

    let top = shouldOpenUp
      ? buttonRect.top - menuRect.height - spacing
      : buttonRect.bottom + spacing;

    let left = buttonRect.right - menuRect.width;

    if (left + menuRect.width > viewportWidth - spacing) {
      left = viewportWidth - menuRect.width - spacing;
    }

    if (left < spacing) {
      left = spacing;
    }

    if (top < spacing) {
      top = buttonRect.bottom + spacing;
      shouldOpenUp = false;
    }

    if (top + menuRect.height > viewportHeight - spacing) {
      top = Math.max(spacing, viewportHeight - menuRect.height - spacing);
    }

    setOpenDirection(shouldOpenUp ? 'up' : 'down');
    setMenuStyles({ top, left });
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updatePosition();

    const handleReposition = () => updatePosition();

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updatePosition]);

  return (
    hasActiveChildren && (
      <Menu as="div" className={baseClass}>
        {({ open }) => (
          <>
            <Menu.Button ref={buttonRef} className="flex items-center justify-center">
              <Icon className="w-4 h-4 ml-4 text-gray-400 transition-colors group-hover:text-gray-500" />
            </Menu.Button>
            {isMounted && createPortal(
              <MenuItemsPortal
                isOpen={open}
                extraClass={extraClass}
                classExtra={classExtra}
                openDirection={openDirection}
                menuStyles={menuStyles}
                onItemsRef={(node) => {
                  itemsRef.current = node;
                  if (node) {
                    setIsOpen(true);
                    requestAnimationFrame(() => updatePosition());
                  } else {
                    setIsOpen(false);
                  }
                }}
              >
                {children}
              </MenuItemsPortal>,
              document.body
            )}
          </>
        )}
      </Menu>
    )
  );
}

function MenuItemsPortal({
  isOpen,
  extraClass,
  classExtra,
  openDirection,
  menuStyles,
  onItemsRef,
  children,
}) {
  return (
    <Transition
      show={isOpen}
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items
        ref={onItemsRef}
        static
        className={joinClassNames(
          extraClass,
          openDirection === 'up' ? 'origin-bottom-right' : 'origin-top-right',
          'fixed text-left w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none pointer-events-auto',
          classExtra,
          'z-[2000]'
        )}
        style={isOpen ? { top: menuStyles.top, left: menuStyles.left } : undefined}
      >
        <div className="py-1">
          {React.Children.map(children, (child) => child && (
            <Menu.Item>
              {({ active }) => (
                <div
                  className={joinClassNames(
                    active
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-700',
                    'px-4 py-2 text-sm flex flex-col'
                  )}
                >
                  {child}
                </div>
              )}
            </Menu.Item>
          ))}
        </div>
      </Menu.Items>
    </Transition>
  );
}
